import { useEffect, useRef, useState } from "react";
import { Moon, Settings, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { LanguageSwitcher } from "./LanguageSwitcher";

export function HeaderSettingsMenu() {
  const { t } = useTranslation();
  const { setTheme, resolvedTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const isDark = mounted && resolvedTheme === "dark";

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={t.header.settings}
        className="rhythm-icon-btn"
      >
        <Settings size={18} />
      </button>

      {isOpen && (
        <div className="card-shadow absolute right-0 top-full z-50 mt-3 w-[min(13rem,calc(100vw-2rem))] rounded-2xl border border-border bg-card p-3 sm:w-52">
          <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-[0.12em] text-muted">
            {t.header.settings}
          </p>
          <button
            type="button"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-card-muted"
          >
            {isDark ? <Sun size={16} className="text-primary" /> : <Moon size={16} className="text-primary" />}
            {isDark ? t.settings.themeLight : t.settings.themeDark}
          </button>
          <div className="mt-2 px-2">
            <LanguageSwitcher />
          </div>
        </div>
      )}
    </div>
  );
}
